import { motion } from "framer-motion";

import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { IconBadge } from "@/components/common/IconBadge";
import type { IconCard } from "@/types/content";
import { staggerContainer, staggerItem, cardHover, viewportOnce } from "@/lib/motion";

type Tone = "emerald" | "blue" | "teal";

interface IconCardGridProps {
  items: IconCard[];
  columns?: 2 | 3;
  tone?: Tone;
  testId?: string;
  cardTestId?: string;
}

const colClasses: Record<2 | 3, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
};

/** Reusable, animated grid of icon cards used across audience/quality sections. */
export function IconCardGrid({
  items,
  columns = 3,
  tone = "teal",
  cardTestId,
}: IconCardGridProps) {
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      className={`grid grid-cols-1 gap-6 ${colClasses[columns]}`}
    >
      {items.map((item) => (
        <motion.div
          key={item.title}
          variants={staggerItem}
          whileHover={{ y: -3 }}
          transition={cardHover}
          className="group flex flex-col rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow duration-200 hover:shadow-md"
          data-testid={cardTestId}
        >
          <IconBadge icon={item.icon} tone={tone} />
          <h3 className="mt-5 text-lg font-semibold text-foreground">{item.title}</h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
            {item.description}
          </p>
        </motion.div>
      ))}
    </motion.div>
  );
}

interface CardGridSectionProps extends IconCardGridProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  muted?: boolean;
  sectionTestId?: string;
}

/** Convenience wrapper: SectionHeading + IconCardGrid inside a Section. */
export function CardGridSection({
  eyebrow,
  title,
  description,
  muted = false,
  sectionTestId,
  ...gridProps
}: CardGridSectionProps) {
  return (
    <Section muted={muted} data-testid={sectionTestId}>
      <Container>
        {title && (
          <SectionHeading eyebrow={eyebrow} title={title} description={description} />
        )}
        <div className={title ? "mt-12" : ""}>
          <IconCardGrid {...gridProps} />
        </div>
      </Container>
    </Section>
  );
}
